/**
 * Artikel (KONZEPT.md §9.1) -- und die strikte Trennung von Stammdaten und
 * Zustand.
 *
 * `Artikel` enthaelt nur, was sich durch Pflege aendert: Bezeichnung, Art,
 * Pruefungen. **Wo** der Artikel liegt und **in welchem Zustand** er ist,
 * steht ausschliesslich in `Zustand` -- und der wird aus dem Buchungslog
 * berechnet, nie geschrieben.
 *
 * Diese Trennung ist Absicht. Ein Modul, das `Artikel` in der Hand haelt, hat
 * kein Feld, in das es einen Status schreiben koennte (CLAUDE.md §3.1 Regel 1).
 * Der Compiler erzwingt damit, was sonst nur eine Hausregel waere.
 */
import { z } from "zod";
import { Id, Inventarnummer, KurzText, LangText, ThwInventarnummer, Zeitpunkt } from "./basis.js";
import { ArtikelArt, Herkunft, Verbleib } from "./enums.js";
/**
 * Eine vorgeschriebene Pruefung an einem Geraet.
 *
 * Bewusst eine Liste und kein einzelnes Feld: Die Ausstattungslisten des THW
 * fuehren dasselbe Geraet mehrfach, einmal je vorgeschriebener Pruefung -- ein
 * LKW etwa unter "EX-Fahrzeuge (DGUV-V 70)" und unter "EX-Nutzungsdauer
 * Reifen". `KONZEPT.md` §9.1 nennt nur ein Feld je Pruefangabe; das haelt den
 * echten Daten nicht stand.
 *
 * `art` ist Freitext, weil die Pruefungsverwaltung beliebige Bezeichnungen
 * liefert ("BG-Rettungssitzgurt", "BG-Bandschlinge") und keine feste Liste.
 */
export const Pruefung = z.object({
    art: KurzText,
    /** Tag der letzten Durchfuehrung, ISO-Datum ohne Uhrzeit. */
    letzte: z.iso.date().nullable(),
    /** Tag der naechsten Faelligkeit. */
    faellig: z.iso.date().nullable(),
    /** Ergebnis im Wortlaut der Liste, z. B. "i. O.". */
    ergebnis: z.string().trim().max(120).nullable(),
});
/** Stammdaten eines Artikels. Enthaelt **keinen** Zustand -- siehe oben. */
export const Artikel = z.object({
    id: Id,
    inventarnummer: Inventarnummer,
    /**
     * Was tatsaechlich im QR- oder Barcode steht, falls es von der
     * Inventarnummer abweicht. Leer, solange das Etikett die Nummer traegt.
     */
    qr_inhalt: z.string().trim().max(512).nullable(),
    bezeichnung: KurzText,
    beschreibung: LangText.nullable(),
    art: ArtikelArt,
    /** Einheit bei Verbrauchsmaterial: Stueck, Meter, Beutel, Rolle. */
    einheit: z.string().trim().max(32).nullable(),
    /** Schwelle fuer die Nachbestellwarnung (KONZEPT.md §12.6). */
    mindestbestand: z.number().int().nonnegative().nullable(),
    /** Sachnummer des THW -- bezeichnet den Artikeltyp, nicht das Einzelgeraet. */
    sachnummer: z.string().trim().max(64).nullable(),
    /** Geraetenummer des Herstellers, etwa eine Fahrzeug-Identnummer. */
    geraetenummer: z.string().trim().max(64).nullable(),
    /**
     * Amtliches Kennzeichen bei Fahrzeugen.
     *
     * Ergaenzung gegenueber KONZEPT.md §9.1: Die Ausstattungsliste fuehrt es,
     * und im Gespraech heisst ein LKW beim Kennzeichen und nicht bei seiner
     * Inventarnummer.
     */
    kennzeichen: z.string().trim().max(32).nullable(),
    hersteller: z.string().trim().max(200).nullable(),
    baujahr: z.string().trim().max(8).nullable(),
    /** Zustaendige Einheit laut Ausstattungsliste, z. B. "1. TZ". */
    einheit_zustaendig: z.string().trim().max(120).nullable(),
    pruefungen: z.array(Pruefung),
    tags: z.array(KurzText),
    herkunft: Herkunft,
    notizen: LangText.nullable(),
    /**
     * Der Stammplatz: der Lagerort oder das Packstueck, wo der Artikel
     * hingehoert (ADR 0013 §6). Kein Zustand, sondern ein Stammdatum -- **wo er
     * ist**, sagt weiter nur der `Zustand`.
     *
     * Der Stammplatz ist der Soll-Inhalt (KONZEPT.md §14.1): Was hier seinen
     * Stammplatz hat, fehlt, solange es nicht hier ist, und „Alles da“ bringt es
     * zurueck. Leer heisst: kein Stammplatz, und „Alles da“ laesst den Artikel in
     * Ruhe.
     *
     * Nur ausdruecklich gesetzt, von jedem angemeldeten Benutzer, nur mit Netz
     * (`StammplatzSetzen`). Anlegen und Aendern der Stammdaten beruehren ihn
     * nicht. Seit Vertrag 2.6.
     */
    stammplatz_id: Id.nullable(),
    angelegt_am: Zeitpunkt,
});
/**
 * Der abgeleitete Zustand eines Artikels.
 *
 * Ergebnis von `zustandBerechnen` aus `@thw/core-domain`. Er wird als
 * Lesecache gespiegelt (CLAUDE.md §3.2), ist aber niemals die Quelle der
 * Wahrheit und muss jederzeit aus dem Log rekonstruierbar sein.
 */
export const Zustand = z.object({
    /** Wo der Artikel ist. Folgt allein aus der Art der Buchungen. */
    verbleib: Verbleib,
    /**
     * In welchem Zustand er ist -- Schluessel eines Status aus den Stammdaten.
     * Ohne gesetzten Status gilt der Standardstatus.
     *
     * Beim Lesen bewusst nur als Zeichenkette geprueft: Ein Status kann
     * stillgelegt sein, und was im Log steht, muss lesbar bleiben.
     */
    status: z.string(),
    /** Der Status zaehlt als einsatzbereit -- unabhaengig davon, wo der Artikel ist. */
    einsatzbereit: z.boolean(),
    /**
     * Der Status archiviert den Artikel (KONZEPT.md §7.2, etwa verloren oder
     * gestohlen). Der Status bleibt als Grund sichtbar, der Artikel faellt aber
     * aus allen aktiven Listen und Zaehlungen.
     */
    archiviert: z.boolean(),
    /**
     * Greifbar: einsatzbereit, im Lager und nicht archiviert. Das ist die Zahl,
     * die an der Einsatzstelle zaehlt.
     */
    verfuegbar: z.boolean(),
    /**
     * Realer Lagerort, oder `null` bei `im_einsatz` und `ausgeliehen`.
     *
     * Ausnahme seit Vertrag 2.4: Steckt der Artikel in einem Packstueck, bleibt
     * hier dessen Lagerort stehen, auch wenn das Packstueck im Einsatz oder
     * verliehen ist (ADR 0012). Wo er dann ist, sagen `verbleib` und der Weg
     * ueber das Packstueck.
     */
    lagerort_id: Id.nullable(),
    einsatzstelle_id: Id.nullable(),
    /** Bestand bei Verbrauchsmaterial; bei Stueckgut immer `null`. */
    bestand: z.number().int().nullable(),
    /** Die Buchung, aus der dieser Zustand hervorgeht. `null` = nie gebucht. */
    letzte_buchung_id: Id.nullable(),
    letzte_buchung_am: Zeitpunkt.nullable(),
});
/** Was die API ausliefert: Stammdaten plus berechneter Zustand. */
export const ArtikelMitZustand = z.object({
    artikel: Artikel,
    zustand: Zustand,
});
/**
 * Stammdaten beim Anlegen. Ohne `id` und `angelegt_am` -- die vergibt der
 * Server, weil ein Artikel anders als eine Buchung nicht offline entsteht.
 *
 * Die Nummer muss hier eine echte THW-Inventarnummer sein. Die Kennung als
 * Nummer vergibt nur der Core, beim Anlegen eines Packstuecks
 * (`PackstueckAnlegen`) -- so faellt eine vertippte Nummer weiter sofort auf.
 */
export const ArtikelAnlegen = Artikel.omit({
    id: true,
    angelegt_am: true,
    stammplatz_id: true,
}).extend({
    inventarnummer: ThwInventarnummer,
    qr_inhalt: z.string().trim().max(512).nullable().default(null),
    beschreibung: LangText.nullable().default(null),
    art: ArtikelArt.default("geraet"),
    einheit: z.string().trim().max(32).nullable().default(null),
    mindestbestand: z.number().int().nonnegative().nullable().default(null),
    sachnummer: z.string().trim().max(64).nullable().default(null),
    geraetenummer: z.string().trim().max(64).nullable().default(null),
    kennzeichen: z.string().trim().max(32).nullable().default(null),
    hersteller: z.string().trim().max(200).nullable().default(null),
    baujahr: z.string().trim().max(8).nullable().default(null),
    einheit_zustaendig: z.string().trim().max(120).nullable().default(null),
    pruefungen: z.array(Pruefung).default([]),
    tags: z.array(KurzText).default([]),
    herkunft: Herkunft.default("manuell"),
    notizen: LangText.nullable().default(null),
});
/**
 * Ein Geraet nach einem unbekannten Scan anlegen (ADR 0026) -- jeder
 * angemeldete Benutzer, nur mit Netz, nur das Noetigste.
 *
 * Eine Ausnahme von ADR 0022: Das Geraet traegt `herkunft: "manuell"`, und
 * der naechste Import aus THWin ist die Wahrheit. Eingebucht wird danach
 * ueber die Warteschlange, wie jede Buchung. Seit Vertrag 2.11.
 */
export const GeraetErfassen = z.object({
    inventarnummer: ThwInventarnummer,
    bezeichnung: KurzText,
    /**
     * Der gelesene Code, so wie er im Etikett stand. Weicht er von der Nummer
     * ab, kommt er nach `qr_inhalt` -- dann findet der naechste Scan das Geraet.
     */
    code: z.string().trim().max(512).nullable().default(null),
});
/**
 * Stammdaten aendern. Alle Felder freiwillig -- was nicht geschickt wird,
 * bleibt stehen.
 *
 * `inventarnummer` fehlt bewusst: Sie ist die Kennung, unter der das Geraet in
 * den Listen des THW und auf seinem Etikett steht. Eine Nummer zu aendern
 * heisst in der Praxis, dass es das falsche Geraet war -- dann gehoert ein
 * neuer Artikel angelegt und der alte ausgebucht, damit die Historie beider
 * stimmt.
 *
 * `herkunft` fehlt ebenfalls: Sie beschreibt, woher die Daten stammen, und
 * wird vom Import gesetzt, nicht von Hand (siehe ADR 0003).
 *
 * `stammplatz_id` fehlt, weil es dafuer einen eigenen Weg gibt, den jeder
 * Benutzer gehen darf und der Kreise prueft (`StammplatzSetzen`).
 */
export const ArtikelAendern = Artikel.omit({
    id: true,
    angelegt_am: true,
    inventarnummer: true,
    herkunft: true,
    stammplatz_id: true,
}).partial();
/**
 * Den Stammplatz setzen -- fuer einen Artikel oder fuer eine ganze
 * Sammelliste (ADR 0013 §6).
 *
 * Darf jeder angemeldete Benutzer, aber nur mit Netz: Der Stammplatz ist ein
 * Stammdatum (ADR 0006), und nur der Server kann pruefen, dass kein Kreis
 * entsteht -- ein Packstueck, dessen Stammplatz in ihm selbst liegt, braechte
 * „Alles da“ ins Schleudern.
 *
 * Das Ziel ist ein aktiver Lagerort (auch ein Packstueck), nie das
 * Standardziel „Ohne festen Platz“. `null` entfernt den Stammplatz.
 */
export const StammplatzSetzen = z.object({
    artikel_ids: z.array(Id).min(1).max(500),
    stammplatz_id: Id.nullable(),
});
export const StammplatzGesetzt = z.object({
    /** Die geaenderten Artikel, wie sie jetzt sind. */
    artikel: z.array(Artikel),
});
//# sourceMappingURL=artikel.js.map